import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Map from "../../components/common/Map";
import SaveGeometries from "../../components/areas/SaveGeometries";
import RoadSelectComponent from "../../components/areas/RoadSelectComponent";
import LocationSearchBar from "../../components/common/LocationSearchBar";
import { useMapData } from "../../hooks/useMapData";
import { useUrlState } from "../../hooks/useUrlState";
import { BoundingBox, MapGeometry } from "../../services/mapsApi";
import { areaShapeLoader, fitView } from "../../geo/areaShapes";
import { COUNTRIES, areaCountry, roadLinksCountry } from "../../config/countries";
import { NETWORK_LINKS, catalogueCountry } from "../../config/catalogue";
import type { CountryCode, MapType } from "../../config/countries";
import { WebMercatorViewport } from "@deck.gl/core";
import NavBar from "../../components/common/NavBar";

const SAMPLE_DATA: Array<{
  position: [number, number];
  population: number;
  name: string;
}> = [];

const ZOOM_THRESHOLDS: Record<string, number> = {
  osm_links: 15,
  network_links: 15,
  statens_vegvesen: 15,
};

// Road links load above zoom 15, so framing may close in to 16 (the default maximum, 14, would stop short of it).
const ROAD_FIT_MAX_ZOOM = 16;

const RoadLinksSelectPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Set once a save succeeds, so closing the dialog afterwards returns to the Areas section.
  const savedRef = useRef(false);
  const { filterState, addGridId, removeGridId } = useUrlState();
  const [selectedPoints, setSelectedPoints] = useState<typeof SAMPLE_DATA>([]);
  const [selectedRoads, setSelectedRoads] = useState<MapGeometry[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(10);
  const [viewState, setViewState] = useState<any>(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState<boolean>(false);
  const [selectedRoadType, setSelectedRoadType] = useState<string>("");
  const [editMode, setEditMode] = useState(false);
  const [savedArea, setSavedArea] = useState<any>(null);
  // The country whose road links this page serves: the saved area's, else the registry's road-link country.
  const country: CountryCode = (savedArea && areaCountry(savedArea)) || roadLinksCountry();
  // Road links on the catalogue's network are read from the catalogue and saved with its country code ('no').
  const onCatalogue = selectedRoadType === NETWORK_LINKS;
  const savedCountry = onCatalogue ? catalogueCountry(country) : country;
  const { geometries, loading, error, fetchMapData, clearData, setGeometries } =
    useMapData();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastApiCenterRef = useRef<{
    longitude: number;
    latitude: number;
  } | null>(null);
  const loadedBoundingBoxRef = useRef<BoundingBox | null>(null);
  const isInteractingRef = useRef<boolean>(false);

  const onPointClick = useCallback((info: any) => {
    if (info.object) {
      const point = info.object;
      setSelectedPoints((prev) => {
        const exists = prev.find((p) => p.name === point.name);
        if (exists) {
          return prev.filter((p) => p.name !== point.name);
        }
        return [...prev, point];
      });
    }
  }, []);

  const onRoadClick = useCallback(
    (info: any) => {
      console.log("Road clicked:", info);
      if (info.object && info.object.id) {
        const clickedRoadId = info.object.id;

        let roadGeometry = geometries.find((g) => g.geo_id === clickedRoadId);
        if (!roadGeometry) {
          roadGeometry = selectedRoads.find((g) => g.geo_id === clickedRoadId);
        }

        if (roadGeometry) {
          const exists = selectedRoads.find((g) => g.geo_id === clickedRoadId);
          if (exists) {
            setSelectedRoads((prev) =>
              prev.filter((g) => g.geo_id !== clickedRoadId)
            );
            removeGridId(clickedRoadId);
          } else if (selectedRoads.length < 50) {
            setSelectedRoads((prev) => [...prev, roadGeometry!]);
            addGridId(clickedRoadId);
          } else {
            alert("Maximum 50 roads can be selected for analysis");
          }
        }
      }
    },
    [geometries, selectedRoads, addGridId, removeGridId]
  );

  const handleRoadRemove = useCallback(
    (roadId: string) => {
      setSelectedRoads((prev) => prev.filter((g) => g.geo_id !== roadId));
      removeGridId(roadId);
    },
    [removeGridId]
  );

  const handleRoadsExport = useCallback(() => {
    setIsSaveModalOpen(true);
  }, []);

  const handleAddById = useCallback(
    async (ids: string[]) => {
      if (!selectedRoadType || ids.length === 0) {
        return;
      }

      try {
        // Fetch geometries for the provided IDs
        const results = await areaShapeLoader.load({
          id: `ids:${savedCountry}:${selectedRoadType}`,
          type: selectedRoadType,
          ids,
          country_code: savedCountry,
        });

        if (results.length === 0) {
          alert('No roads found for the provided ID(s)');
          return;
        }

        // Filter out IDs that are already selected
        const newGeometries = results.filter(
          (geom) => !selectedRoads.find((g) => g.geo_id === geom.geo_id)
        );

        if (newGeometries.length === 0) {
          alert('All provided IDs are already selected');
          return;
        }

        // Check if adding these would exceed the limit
        if (selectedRoads.length + newGeometries.length > 50) {
          alert(
            `Cannot add ${newGeometries.length} road(s). Maximum 50 roads can be selected. Currently ${selectedRoads.length} selected.`
          );
          return;
        }

        // Add to selected roads
        setSelectedRoads((prev) => [...prev, ...newGeometries]);

        // Add to geometries if not already present
        const existingIds = new Set(geometries.map((g) => g.geo_id));
        const toAdd = newGeometries.filter((g) => !existingIds.has(g.geo_id));
        if (toAdd.length > 0) {
          setGeometries([...geometries, ...toAdd]);
        }

        // Add to URL state
        newGeometries.forEach((geom) => {
          addGridId(geom.geo_id);
        });

        // Auto-zoom to the newly added geometries
        setViewState({
          ...fitView(newGeometries.map((g) => g.geometry), country, { maxZoom: ROAD_FIT_MAX_ZOOM }),
          pitch: 0,
          bearing: 0,
          transitionDuration: 1000,
        });

        console.log(`Successfully added ${newGeometries.length} roads by ID`);
      } catch (error) {
        console.error('Failed to fetch roads by ID:', error);
        throw error;
      }
    },
    [selectedRoadType, selectedRoads, geometries, addGridId, setGeometries, country, savedCountry]
  );

  const handleSaveReport = useCallback(
    (name: string, description: string) => {
      savedRef.current = true;
      console.log("Saving report:", {
        name,
        description,
        roadIds: filterState.gridIds,
      });
    },
    [filterState.gridIds]
  );

  const handleCloseSaveModal = useCallback(() => {
    setIsSaveModalOpen(false);
    if (savedRef.current) {
      savedRef.current = false;
      navigate("/?section=areas");
    }
  }, [navigate]);

  const handleRoadTypeChange = useCallback(
    (roadTypeId: string) => {
      setSelectedRoadType(roadTypeId);
      // Clear current selection when changing road types
      setSelectedRoads([]);
      // Clear all selected road IDs from URL state
      filterState.gridIds.forEach(roadId => removeGridId(roadId));
      // Clear geometries when changing road types
      clearData();
      // Clear cached loaded area
      loadedBoundingBoxRef.current = null;
      lastApiCenterRef.current = null;
      console.log("Road type changed to:", roadTypeId);
    },
    [clearData, filterState.gridIds, removeGridId]
  );

  const handleLocationSearch = useCallback((lat: number, lng: number) => {
    setViewState((prevState: any) => ({
      ...prevState,
      longitude: lng,
      latitude: lat,
      zoom: 14,
      transitionDuration: 1000,
    }));
  }, []);

  const isViewWithinLoadedArea = useCallback(
    (currentBoundingBox: BoundingBox) => {
      if (!loadedBoundingBoxRef.current) {
        return false;
      }

      const loaded = loadedBoundingBoxRef.current;
      const current = currentBoundingBox;

      // Check if current view is entirely within the loaded bounding box with some margin
      const margin = 0.01;
      return (
        current.lng1 >= loaded.lng1 - margin &&
        current.lng2 <= loaded.lng2 + margin &&
        current.lat1 >= loaded.lat1 - margin &&
        current.lat2 <= loaded.lat2 + margin
      );
    },
    []
  );

  const onViewStateChange = useCallback((newViewState: any) => {
    setCurrentZoom(newViewState.zoom);
    setViewState(newViewState);

    isInteractingRef.current = true;

    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    interactionTimeoutRef.current = setTimeout(() => {
      isInteractingRef.current = false;
    }, 300);
  }, []);

  const getBoundingBox = useCallback(() => {
    if (!viewState || !viewState.width || !viewState.height) {
      return null;
    }

    const viewport = new WebMercatorViewport(viewState);

    const [west, south] = viewport.unproject([0, viewState.height]);
    const [east, north] = viewport.unproject([viewState.width, 0]);

    return {
      lng1: west,
      lat1: south,
      lng2: east,
      lat2: north,
    };
  }, [viewState]);

  useEffect(() => {
    const state = location.state as {
      editMode?: boolean;
      savedArea?: any;
      geometries?: MapGeometry[];
    } | null;
    console.log("RoadLinksSelectPage received state:", state);
    if (state?.savedArea && state?.geometries) {
      console.log("Processing saved area:", state.savedArea);
      console.log("Processing geometries:", state.geometries);

      if (state.editMode) {
        setEditMode(true);
        setSavedArea(state.savedArea);
      }

      // Set the road type from the saved area
      console.log("Setting road type to:", state.savedArea.geo_type || state.savedArea.type);
      setSelectedRoadType(state.savedArea.geo_type || state.savedArea.type);

      const transformedGeometries = state.geometries.map((g: any) => {
        console.log("Transforming geometry:", g);
        return {
          geo_id: String(g.geo_id || g.road_id),
          geometry: g.geometry,
        };
      });
      console.log("Transformed geometries:", transformedGeometries);

      const savedAreaRoads = transformedGeometries.filter(
        (g) =>
          state.savedArea.ids.includes(Number(g.geo_id)) ||
          state.savedArea.ids.includes(g.geo_id)
      );
      console.log("Filtered saved area roads:", savedAreaRoads);

      setSelectedRoads(savedAreaRoads);

      savedAreaRoads.forEach((road) => {
        addGridId(road.geo_id);
      });

      // Always load saved geometries regardless of zoom level
      setGeometries(transformedGeometries);

      // Frame the map on the selected roads
      if (savedAreaRoads.length > 0) {
        setViewState({
          ...fitView(savedAreaRoads.map((g) => g.geometry), areaCountry(state.savedArea), { maxZoom: ROAD_FIT_MAX_ZOOM }),
          pitch: 0,
          bearing: 0,
          transitionDuration: 1000,
        });
      }
    }
  }, [location.state, setGeometries, addGridId]);

  const debouncedFetchMapData = useCallback(
    (
      boundingBox: BoundingBox,
      center: { longitude: number; latitude: number }
    ) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (
          !isInteractingRef.current &&
          selectedRoadType &&
          ["osm_links", NETWORK_LINKS].includes(selectedRoadType)
        ) {
          // Expand bounding box by 50% to load more area for future panning
          const lngRange = boundingBox.lng2 - boundingBox.lng1;
          const latRange = boundingBox.lat2 - boundingBox.lat1;
          const expandedBoundingBox = {
            lng1: boundingBox.lng1 - lngRange * 0.25,
            lat1: boundingBox.lat1 - latRange * 0.25,
            lng2: boundingBox.lng2 + lngRange * 0.25,
            lat2: boundingBox.lat2 + latRange * 0.25,
          };

          fetchMapData(expandedBoundingBox, selectedRoadType as MapType, country, selectedRoadType === NETWORK_LINKS);
          lastApiCenterRef.current = center;
          loadedBoundingBoxRef.current = expandedBoundingBox;
        }
      }, 500);
    },
    [fetchMapData, selectedRoadType, country]
  );

  useEffect(() => {
    const zoomThreshold = ZOOM_THRESHOLDS[selectedRoadType] || 15;

    if (currentZoom > zoomThreshold && viewState) {
      const boundingBox = getBoundingBox();

      if (boundingBox) {
        const center = {
          longitude: viewState.longitude,
          latitude: viewState.latitude,
        };

        // Only fetch if current view is outside the loaded area
        if (!isViewWithinLoadedArea(boundingBox as BoundingBox)) {
          debouncedFetchMapData(boundingBox as BoundingBox, center);
        }
      }
    } else if (currentZoom <= zoomThreshold) {
      clearData();
      lastApiCenterRef.current = null;
      loadedBoundingBoxRef.current = null;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    }
  }, [
    currentZoom,
    viewState,
    selectedRoadType,
    getBoundingBox,
    isViewWithinLoadedArea,
    debouncedFetchMapData,
    clearData,
  ]);

  useEffect(() => {
    if (filterState.gridIds.length === 0 && !location.state) {
      setSelectedRoads([]);
    }
  }, [filterState.gridIds, location.state]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="h-screen flex">
      <div className="flex-1 flex flex-col">
        <NavBar
          title={
            editMode
              ? `Edit: ${savedArea?.name || "Road Links"}`
              : "Road Links"
          }
          showBackButton={true}
        />

        <div className="flex-1 relative">
          <Map
            data={SAMPLE_DATA}
            selectedPoints={selectedPoints}
            onPointClick={onPointClick}
            onViewStateChange={onViewStateChange}
            viewState={viewState}
            geometries={geometries}
            loading={loading}
            selectedGrids={selectedRoads}
            onGridClick={onRoadClick}
            initialView={COUNTRIES[country].defaultView}
          />
          <LocationSearchBar
            onSearch={handleLocationSearch}
            country={country}
            placeholder={`Search an address in ${COUNTRIES[country].label}`}
          />
        </div>

        <div className="bg-surface border-t border-paper-300 px-4 py-2 flex-shrink-0">
          <div className="max-w-full px-4 sm:px-6 lg:px-8">
            <div className="text-sm text-ink-muted">
              <div className="flex items-center space-x-4">
                {(() => {
                  const threshold = ZOOM_THRESHOLDS[selectedRoadType] || 15;
                  return currentZoom <= threshold ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-warn-ink font-medium">
                        📍 Zoom in to level {threshold}+ to view{" "}
                        {selectedRoadType ? "roads" : "road links"}
                      </span>
                      <span className="text-ink-subtle text-sm">
                        (Current: {currentZoom.toFixed(1)})
                      </span>
                    </div>
                  ) : (
                    <span>
                      Current Zoom Level:{" "}
                      <span className="font-medium text-ink-soft">
                        {currentZoom.toFixed(2)}
                      </span>
                    </span>
                  );
                })()}
                {loading && (
                  <span className="text-xs text-note-ink">
                    Loading road geometries...
                  </span>
                )}
                {error && (
                  <span className="text-xs text-bad-ink">Error: {error}</span>
                )}
              </div>
              {currentZoom > (ZOOM_THRESHOLDS[selectedRoadType] || 15) &&
                getBoundingBox() && (
                  <div className="mt-1">
                    <span className="text-xs text-ink-subtle">
                      Bounding Box:{" "}
                    </span>
                    <span className="font-mono text-xs text-ink-soft">
                      lng1: {getBoundingBox()?.lng1.toFixed(6)}, lat1:{" "}
                      {getBoundingBox()?.lat1.toFixed(6)}, lng2:{" "}
                      {getBoundingBox()?.lng2.toFixed(6)}, lat2:{" "}
                      {getBoundingBox()?.lat2.toFixed(6)}
                    </span>
                  </div>
                )}
              {geometries.length > 0 && (
                <div className="mt-1">
                  <span className="text-xs text-ok-ink">
                    Loaded {geometries.length} road geometries
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <RoadSelectComponent
        selectedRoadType={selectedRoadType}
        onRoadTypeChange={handleRoadTypeChange}
        selectedRoads={selectedRoads}
        onRoadRemove={handleRoadRemove}
        onRoadsExport={handleRoadsExport}
        onAddById={handleAddById}
      />

      <SaveGeometries
        isOpen={isSaveModalOpen}
        onClose={handleCloseSaveModal}
        selectedIds={filterState.gridIds}
        selectedMapType={selectedRoadType}
        onSave={handleSaveReport}
        countryCode={savedCountry}
        editArea={
          editMode && savedArea
            ? { id: savedArea.id, name: savedArea.name, description: savedArea.description }
            : undefined
        }
      />
    </div>
  );
};

export default RoadLinksSelectPage;
